import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import DashboardLayout from "../../components/customer/DashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import {
  paymentService,
  GetPaymentsData,
} from "../../services/payment.service";
import { orderService } from "../../services/order.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCreditCard,
  faCircleNotch,
  faTriangleExclamation,
  faSearch,
  faEye,
  faClipboardList,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/pages/MyPaymentsPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";
import Swal from "sweetalert2";

const MyPaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<GetPaymentsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [searchText, setSearchText] = useState("");

  // Default dates: Last 7 days for payments
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(formatDate(sevenDaysAgo));
  const [endDate, setEndDate] = useState(formatDate(today));

  const fetchPayments = async (page: number, size: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getPayments({
        page,
        pageSize: size,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        SearchString: searchText || undefined,
      });

      if (response.statusCode === 200) {
        setPayments(response.data);
        // Si no hay totalCount, aproximamos el total de filas usando totalPages
        setTotalRows(response.pagination.totalPages * size);
      } else {
        setError(response.message || "Error al cargar los pagos.");
      }
    } catch (err: any) {
      console.error("Error fetching payments:", err);
      // Handle 404 as "No results" instead of a hard error
      if (err.response?.status === 404) {
        setPayments([]);
        setTotalRows(0);
      } else {
        setError("Ocurrió un error inesperado al cargar tus pagos.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(1, perPage);
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchPayments(page, perPage);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    fetchPayments(page, newPerPage);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPayments(1, perPage);
  };

  const handleDownloadPdf = async (idOrder: number) => {
    try {
      const response = await orderService.getOrderSummaryPdf(idOrder);
      if (response.statusCode === 200 && response.base64Pdf) {
        const linkSource = `data:application/pdf;base64,${response.base64Pdf}`;
        const downloadLink = document.createElement("a");
        const fileName = `Invoice_${idOrder}.pdf`;

        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo generar el PDF.",
        });
      }
    } catch (error: any) {
      console.error("Error downloading PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un error al generar el PDF.",
      });
    }
  };

  const columns: TableColumn<GetPaymentsData>[] = [
    {
      name: "Fecha",
      selector: (row) => row.createdDate.toString(),
      sortable: true,
      cell: (row) => {
        const date = new Date(row.createdDate);
        return isNaN(date.getTime())
          ? row.createdDate.toString()
          : date.toLocaleDateString() +
              " " +
              date.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              });
      },
      width: "180px",
    },
    {
      name: "Tracking Relacionado",
      selector: (row) => row.tracking,
      sortable: true,
      cell: (row) => (
        <span className={styles.trackingPill}>{row.tracking || "N/A"}</span>
      ),
    },
    {
      name: "Monto",
      selector: (row) => row.amount,
      sortable: true,
      cell: (row) => (
        <div className={styles.amountText}>
          {row.amount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          <span className={styles.currencyBadge}>
            {row.currency.toUpperCase()}
          </span>
        </div>
      ),
      right: true,
    },
    {
      name: "Estado de Pago",
      selector: (row: GetPaymentsData) => {
        const isVoided = Boolean(row.isVoided);
        const isRefunded = Boolean(row.isRefunded);
        const isPaid = Boolean(row.isPaid);

        if (isVoided && isRefunded) return "Reembolsada";
        if (isVoided && !isRefunded) return "Reembolso Pendiente";
        if (isPaid) return "Pagada";
        return "Pago Pendiente";
      },
      sortable: true,
      cell: (row: GetPaymentsData) => {
        const isVoided = Boolean(row.isVoided);
        const isRefunded = Boolean(row.isRefunded);
        const isPaid = Boolean(row.isPaid);

        let label = "Pago Pendiente";
        let badgeStyle: React.CSSProperties = {
          backgroundColor: "#fef3c7",
          color: "#92400e",
          padding: "4px 10px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: 600,
          display: "inline-block",
        };

        if (isVoided && isRefunded) {
          label = "Reembolsada";
          badgeStyle = { ...badgeStyle, backgroundColor: "#e0e7ff", color: "#3730a3" };
        } else if (isVoided && !isRefunded) {
          label = "Reembolso Pendiente";
          badgeStyle = { ...badgeStyle, backgroundColor: "#ffedd5", color: "#c2410c" };
        } else if (isPaid) {
          label = "Pagada";
          badgeStyle = { ...badgeStyle, backgroundColor: "#d1fae5", color: "#065f46" };
        } else {
          label = "Pago Pendiente";
          badgeStyle = { ...badgeStyle, backgroundColor: "#fef3c7", color: "#92400e" };
        }

        return <span style={badgeStyle}>{label}</span>;
      },
      width: "180px",
    },
    {
      name: "Acciones",
      width: "140px",
      cell: (row) => (
        <div className="d-flex gap-2" style={{ paddingRight: "10px" }}>
          <button
            className={styles.detailButton}
            onClick={(e) => {
              e.stopPropagation();
              if (row.idOrder) {
                window.open(`/customer/order/${row.idOrder}`, "_blank");
              }
            }}
            title="Ver detalle del envío"
          >
            <FontAwesomeIcon icon={faEye} />
          </button>

          {row.idOrder && (
            <button
              className={styles.pdfButton}
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadPdf(row.idOrder);
              }}
              title="Descargar PDF de resumen"
            >
              <FontAwesomeIcon icon={faFilePdf} />
            </button>
          )}
        </div>
      ),
      right: true,
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: "white",
        borderRadius: "8px",
        overflow: "hidden",
      },
    },
    headRow: {
      style: {
        backgroundColor: "var(--elPaket-primary-color)",
        color: "white",
        minHeight: "44px",
      },
    },
    headCells: {
      style: {
        color: "var(--brand-black)",
        fontWeight: "600",
        fontSize: "12px",
        textTransform: "uppercase" as any,
        letterSpacing: "0.5px",
        padding: "12px 16px",
      },
    },
    rows: {
      style: {
        minHeight: "56px",
        fontSize: "14px",
        color: "#374151",
        borderBottom: "1px solid #e5e7eb !important",
        "&:hover": {
          backgroundColor: "#f9fafb !important",
        },
      },
    },
    cells: {
      style: {
        padding: "12px 16px",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #e5e7eb",
        marginTop: "0",
        padding: "8px 0",
      },
    },
  };

  return (
    <DashboardLayout>
      <div className={styles.myPaymentsContainer}>
        <DashboardCard
          icon={faCreditCard}
          title="Mis Pagos"
          description="Historial de transacciones y pagos realizados por tus envíos."
          showVerificationAlerts={true}
        />

        <div className={styles.paymentsTableWrapper}>
          <form className={styles.tableActions} onSubmit={handleSearch}>
            <div className={styles.filterSection}>
              <div className={styles.filterGroup}>
                <label>Desde</label>
                <input
                  type="date"
                  className={styles.filterInput}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className={styles.filterGroup}>
                <label>Hasta</label>
                <input
                  type="date"
                  className={styles.filterInput}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.searchRow}>
              <div className={styles.searchWrapper}>
                <FontAwesomeIcon
                  icon={faSearch}
                  className={styles.searchIcon}
                />
                <input
                  type="text"
                  placeholder="Buscar por tracking..."
                  className={styles.searchInput}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <button type="submit" className={btnStyles.primary}>
                <FontAwesomeIcon icon={faSearch} /> Buscar
              </button>
            </div>
          </form>

          {error ? (
            <div className={styles.paymentsError}>
              <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
              <p>{error}</p>
              <button
                className={btnStyles.primary}
                onClick={() => fetchPayments(currentPage, perPage)}
              >
                Reintentar
              </button>
            </div>
          ) : payments.length === 0 && !loading ? (
            <div className={styles.emptyContainer}>
              <FontAwesomeIcon
                icon={faClipboardList}
                size="3x"
                style={{ marginBottom: "16px", opacity: 0.5 }}
              />
              <p>
                No se encontraron pagos registrados con los filtros aplicados.
              </p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={payments}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={currentPage}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              progressPending={loading}
              progressComponent={
                <div className={styles.paymentsLoading}>
                  <FontAwesomeIcon icon={faCircleNotch} spin size="2x" />
                  <p>Cargando transacciones...</p>
                </div>
              }
              customStyles={customStyles}
              responsive
              persistTableHead
              noDataComponent={null}
              highlightOnHover
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MyPaymentsPage;
